import * as actionsFromCustomerInfo from 'app/core/store/customer-info/customer-info.actions';
import * as actions from 'app/core/store/session/user-context/user-context.actions';
import { Customer, UserContext, UserContextTO } from 'app/core/store/session/user-context/user-context.interface';
import { initialUserContext } from 'app/core/store/session/user-context/user-context.model';
import { util } from 'app/core/util/util';

const EMPTY_NAME = '- no name -';

function userContextLoad(action: actions.UserContextLoadAction, state: UserContext): UserContext {
  return Object.assign({}, state, {
    contextLoading: true,
    contextLoaded: false,
    contextFailed: false,
  });
}

function userContextLoadSuccess(action: actions.UserContextLoadSuccessAction, state: UserContext): UserContext {
  // alert("Change isAdmin to isAgent in user-context.reducer.ts");
  const userContextTO: UserContextTO = action.payload;
  const customersMapper = (customersObject: object) => {
    const customers: Customer[] = customersObject && Object.keys(customersObject).map(id => Object.assign({ id }, customersObject[id]));
    if (customers && customers.length) {
      customers.sort((c1, c2) => (c1.name < c2.name ? -1 : 1));
      const colors = util.generateColors(customers.length);
      customers.forEach((customer, index) => {
        customer.color = colors[index];
        customer.colorType = util.lightOrDark(colors[index]);
      });
    }
    return customers;
  };
  const salesOrganisationsMapper = salesOrganisations =>
    salesOrganisations &&
    Object.keys(salesOrganisations).map(id => {
      return {
        id,
        name: salesOrganisations[id],
      };
    });
  return Object.assign({}, state, {
    contextLoaded: true,
    contextLoading: false,
    contextFailed: false,
    authorized: true,
    id: userContextTO.id,
    email: userContextTO.email,
    firstName: userContextTO.firstName,
    lastName: userContextTO.lastName,
    initials: userContextTO.initials,
    language: userContextTO.language,
    customers: userContextTO.userType === 'ClientUser' ? customersMapper(userContextTO.customers) : null,
    salesOrganisations: userContextTO.userType === 'EpsUser' ? salesOrganisationsMapper(userContextTO.salesOrganisations) : null,
    isAdmin: !!userContextTO.isAdmin,
    isAgent: !!userContextTO.isAgent,
    isEpsUser: userContextTO.userType === 'EpsUser',
    canRelocate: userContextTO.canRelocate,
    canUseHiddenMaterials: !!userContextTO.canUseHiddenMaterials,
    useEmailActors: userContextTO.useEmailActors,
    canAccessHomeMarketBonus: userContextTO.canAccessHomeMarketBonus,
    isSuperUser: userContextTO.isTransporter, // TODO 452 rename in future
    canSelectDepot: userContextTO.canSelectDepot,
    adminRoles: {
      updateEpsUser: userContextTO.isAgent || userContextTO.adminRoles.updateEpsUser,
      updateClientUser: userContextTO.isAgent || userContextTO.adminRoles.updateClientUser,
      createClientUser: userContextTO.isAgent || userContextTO.adminRoles.createClientUser,
      reInvite: userContextTO.isAgent || userContextTO.adminRoles.reInvite,
      inviteClientUser: userContextTO.isAgent || userContextTO.adminRoles.inviteClientUser,
      inviteEpsUser: userContextTO.isAgent || userContextTO.adminRoles.inviteEpsUser,
      resetPassword: userContextTO.isAgent || userContextTO.adminRoles.resetPassword,
    },
  });
}

function userContextLoadFail(action: actions.UserContextLoadFailAction, state: UserContext): UserContext {
  return {
    ...initialUserContext,
    authorized: false,
    contextLoaded: false,
    contextLoading: false,
    contextFailed: true,
  };
}

function customersClear(action: actions.CustomersClearAction, state: UserContext): UserContext {
  return Object.assign({}, state, {
    customers: null,
    customersLoaded: false,
    customersLoading: false,
  });
}

function customersLoad(action: actions.CustomersLoadAction, state: UserContext): UserContext {
  return Object.assign({}, state, {
    customersLoaded: false,
    customersLoading: true,
  });
}

function customersLoadSuccess(action: actions.CustomersLoadSuccessAction, state: UserContext): UserContext {
  return {
    ...state,
    customersLoaded: true,
    customersLoading: false,
    customers: action.payload.items.map(c => {
      return { id: c.customerNumber, name: c.customerName ?? EMPTY_NAME, salesOrganization: c.salesOrganization };
    }),
  }
}

function customersLoadFail(action: actions.CustomersLoadFailAction, state: UserContext): UserContext {
  return Object.assign({}, state, {
    customersLoaded: false,
    customersLoading: false,
    customers: null,
  });
}

function customerGetShipTosClear(action: actions.CustomerGetShipTosClearAction, state: UserContext): UserContext {
  return {
    ...state,
    customers: state.customers
      ? state.customers.map(customer => ({
        id: customer.id,
        name: customer.name,
        role: customer.role,
        salesOrganization: customer.salesOrganization,
      }))
      : null,
  };
}

function customerGetShipTosLoad(action: actions.CustomerGetShipTosLoadAction, state: UserContext): UserContext {
  return state;
}

function customerGetShipTosLoadSuccess(action: actions.CustomerGetShipTosLoadSuccessAction, state: UserContext): UserContext {
  return {
    ...state,
    customers: state.customers
      ? state.customers.map(customer => {
        if (customer.id === action.payload.customerId) {
          return {
            ...customer,
            shipTos: action.payload.shipTos,
          };
        } else {
          return customer;
        }
      })
      : null,
  };
}

function customerGetShipTosLoadFail(action: actions.CustomerGetShipTosLoadFailAction, state: UserContext): UserContext {
  return state;
}

export function reducer(state = initialUserContext, action: actions.Actions | actionsFromCustomerInfo.Actions): UserContext {
  let newState = state;

  switch (action.type) {
    case actions.USERCONTEXT_LOAD:
      newState = userContextLoad(action, state);
      break;
    case actions.USERCONTEXT_LOAD_SUCCESS:
      newState = userContextLoadSuccess(action, state);
      break;
    case actions.USERCONTEXT_LOAD_FAIL:
      newState = userContextLoadFail(action, state);
      break;
    case actions.CUSTOMERS_CLEAR:
      newState = customersClear(action, state);
      break;
    case actions.CUSTOMERS_LOAD:
      newState = customersLoad(action, state);
      break;
    case actions.CUSTOMERS_LOAD_SUCCESS:
      newState = customersLoadSuccess(action, state);
      break;
    case actions.CUSTOMERS_LOAD_FAIL:
      newState = customersLoadFail(action, state);
      break;
    case actions.CUSTOMER_GET_SHIPTOS_CLEAR:
      newState = customerGetShipTosClear(action, state);
      break;
    case actions.CUSTOMER_GET_SHIPTOS_LOAD:
      newState = customerGetShipTosLoad(action, state);
      break;
    case actions.CUSTOMER_GET_SHIPTOS_LOAD_SUCCESS:
      newState = customerGetShipTosLoadSuccess(action, state);
      break;
    case actions.CUSTOMER_GET_SHIPTOS_LOAD_FAIL:
      newState = customerGetShipTosLoadFail(action, state);
      break;
  }

  return newState;
}
